import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiTextAlignLeftLight, PiArrowDownLight, PiArrowUpLight, PiMedalLight, PiCheckSquareLight, PiCheckCircleLight, PiClipboardTextLight, PiCodeLight, PiCopyLight, PiPencilSimpleLight, PiFileTextLight, PiHashLight, PiListChecksLight, PiCircleNotchLight, PiPlusLight, PiMagnifyingGlassLight, PiPaperPlaneRightLight, PiShieldCheckLight, PiSlidersHorizontalLight, PiTrashLight, PiToggleLeftLight, PiTextTLight, PiUsersLight, PiXCircleLight } from 'react-icons/pi';
import { assessmentAPI, candidateAPI, jobAPI } from '../../services/api';
import SkeletonBlock from '../../components/common/SkeletonBlock';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const textareaClass = 'min-h-[96px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const metricTone = {
  sky: 'text-sky-600',
  slate: 'text-slate-800',
  green: 'text-emerald-600',
  amber: 'text-amber-600',
  rose: 'bg-rose-50 text-rose-700'
};

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['reviewed', 'submitted', 'passed'].includes(normalized)) return 'border-emerald-100 text-emerald-600';
  if (['assigned', 'in_progress'].includes(normalized)) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (['timed_out', 'expired'].includes(normalized)) return 'border-amber-100 text-amber-600';
  if (['terminated', 'failed'].includes(normalized)) return 'border-rose-100 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => (
  <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
    <div className="mb-4 flex items-center justify-between gap-3">
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${metricTone[tone] || metricTone.sky}`}>
        <Icon size={24} />
      </span>
      <span className="text-xs font-medium text-slate-400">{detail}</span>
    </div>
    <div className="font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
    <div className="mt-2 text-sm font-medium text-slate-700">{label}</div>
  </div>
);

const Panel = ({ title, icon: Icon, action, children, className = '' }) => (
  <section className={`min-w-0 flex flex-col rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)] ${className}`}>
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex items-center justify-center text-sky-600">
            <Icon size={24} />
          </span>
          <h2 className="truncate font-display text-[17px] font-normal text-[#0b1f3a]">{title}</h2>
        </div>
        {action}
      </div>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const AssessmentsPageSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="space-y-3">
        <SkeletonBlock className="h-3 w-24 rounded-md" />
        <SkeletonBlock className="h-9 w-72 rounded-md" />
        <SkeletonBlock className="h-4 w-[34rem] max-w-full rounded-md" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-3 w-20 rounded-md" />
            </div>
            <SkeletonBlock className="h-8 w-20 rounded-md" />
            <SkeletonBlock className="mt-2 h-4 w-24 rounded-md" />
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section className="min-w-0 rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <SkeletonBlock className="h-8 w-8 rounded-lg" />
                <SkeletonBlock className="h-5 w-40 rounded-md" />
              </div>
              <SkeletonBlock className="h-4 w-24 rounded-md" />
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row">
              <SkeletonBlock className="h-10 flex-1 rounded-lg" />
              <SkeletonBlock className="h-10 w-full rounded-lg lg:w-40" />
              <SkeletonBlock className="h-10 w-full rounded-lg lg:w-40" />
            </div>
            <div className="overflow-x-auto -mx-4 px-4 sm:-mx-5 sm:px-5">
              <div className="min-w-[980px]">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="grid grid-cols-[1.7fr_1.2fr_1.2fr_1.2fr_0.8fr_1fr] gap-4 border-b border-slate-100 py-4 last:border-0">
                    <div className="space-y-2">
                      <SkeletonBlock className="h-4 w-40 rounded-md" />
                      <SkeletonBlock className="h-3 w-28 rounded-md" />
                    </div>
                    <SkeletonBlock className="h-4 w-28 rounded-md" />
                    <SkeletonBlock className="h-4 w-28 rounded-md" />
                    <SkeletonBlock className="h-4 w-28 rounded-md" />
                    <SkeletonBlock className="h-7 w-20 rounded-md" />
                    <div className="flex justify-end gap-1.5">
                      {Array.from({ length: 4 }).map((__, buttonIndex) => <SkeletonBlock key={buttonIndex} className="h-8 w-8 rounded-md" />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="min-w-0 rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-8 w-8 rounded-lg" />
              <SkeletonBlock className="h-5 w-20 rounded-md" />
            </div>
          </div>
          <div className="space-y-4 p-4 sm:p-5">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <SkeletonBlock className="h-4 w-40 rounded-md" />
              <SkeletonBlock className="mt-2 h-3 w-28 rounded-md" />
              <div className="mt-3 grid grid-cols-3 gap-2">
                {Array.from({ length: 3 }).map((_, index) => <SkeletonBlock key={index} className="h-16 rounded-md" />)}
              </div>
            </div>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-slate-100 p-3">
                <SkeletonBlock className="h-4 w-32 rounded-md" />
                <SkeletonBlock className="mt-3 h-16 w-full rounded-md" />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  </div>
);

const Field = ({ label, children, error }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-xs font-medium text-rose-600">{error}</span>}
  </label>
);

const EmptyState = ({ title, copy, icon: Icon = PiClipboardTextLight }) => (
  <div className="flex min-h-[260px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="mb-4 flex items-center justify-center text-sky-600">
      <Icon size={26} />
    </div>
    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{title}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{copy}</p>
  </div>
);

const defaultForm = {
  jobPostingId: '',
  assessmentTitle: 'Role readiness assessment',
  assessmentType: 'role_knowledge',
  description: '',
  instructions: '',
  timeLimitMinutes: 6,
  questionCount: 20,
  passingScore: 70,
  maxAttempts: 1,
  isTimed: true,
  preventCopyPaste: true,
  randomizeQuestions: false,
  showResultsImmediately: true,
  resultVisibility: 'score_and_breakdown',
  questionMode: 'custom',
  questions: []
};

const fallbackAssessmentTypes = [
  { type: 'role_knowledge', label: 'Role Knowledge' },
  { type: 'technical_screen', label: 'Technical Screen' },
  { type: 'coding_challenge', label: 'Coding Challenge' },
  { type: 'language', label: 'Language Assessment' },
  { type: 'situational_judgement', label: 'Situational Judgement' },
  { type: 'culture_fit', label: 'Culture Fit' },
  { type: 'custom', label: 'Custom Assessment' }
];

const questionTypeOptions = [
  { value: 'single_choice', label: 'Single choice', icon: PiListChecksLight, inputType: 'radio', defaultMode: 'auto' },
  { value: 'multiple_choice', label: 'Multiple choice', icon: PiCheckSquareLight, inputType: 'checkbox', defaultMode: 'auto' },
  { value: 'text', label: 'Short answer', icon: PiTextTLight, inputType: 'text', defaultMode: 'auto' },
  { value: 'textarea', label: 'Paragraph', icon: PiTextAlignLeftLight, inputType: 'textarea', defaultMode: 'manual' },
  { value: 'numeric', label: 'Number', icon: PiHashLight, inputType: 'number', defaultMode: 'auto' },
  { value: 'boolean', label: 'True / false', icon: PiToggleLeftLight, inputType: 'toggle', defaultMode: 'auto' },
  { value: 'code', label: 'Code', icon: PiCodeLight, inputType: 'code', defaultMode: 'manual' }
];

const choiceTypes = ['single_choice', 'multiple_choice'];

const compactId = (value, fallback) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return normalized || fallback;
};

const splitLines = (value) => String(value || '')
  .split('\n')
  .map((item) => item.trim())
  .filter(Boolean);

const splitTags = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const optionFrom = (option, index) => {
  if (typeof option === 'string') {
    const id = compactId(option, `o${index + 1}`);
    return { id, label: option, value: id };
  }
  const label = option?.label || option?.value || `Option ${index + 1}`;
  const id = compactId(option?.id || option?.value || label, `o${index + 1}`);
  return { id, label: String(label), value: option?.value === undefined ? id : option.value };
};

const blankOptions = () => ([
  { id: 'o1', label: 'Option 1', value: 'o1' },
  { id: 'o2', label: 'Option 2', value: 'o2' }
]);

const createBlankQuestion = (type = 'single_choice', index = 0) => {
  const meta = questionTypeOptions.find((item) => item.value === type) || questionTypeOptions[0];
  const base = {
    id: `q${index + 1}`,
    prompt: '',
    type: meta.value,
    inputType: meta.inputType,
    points: 1,
    required: true,
    displayOrder: index + 1,
    gradingMode: meta.defaultMode,
    skillTagsText: '',
    explanation: '',
    options: [],
    correctAnswer: '',
    correctAnswers: [],
    acceptableAnswersText: '',
    tolerance: 0,
    caseSensitive: false
  };

  if (meta.value === 'single_choice') {
    return { ...base, options: blankOptions(), correctAnswer: 'o1' };
  }
  if (meta.value === 'multiple_choice') {
    return { ...base, options: blankOptions(), correctAnswers: ['o1'] };
  }
  if (meta.value === 'numeric') {
    return { ...base, correctAnswer: '' };
  }
  if (meta.value === 'boolean') {
    return { ...base, correctAnswer: true };
  }
  if (meta.value === 'text') {
    return { ...base, acceptableAnswersText: '' };
  }
  return base;
};

const normalizeQuestionForForm = (question, index) => {
  const type = question?.type === 'radio'
    ? 'single_choice'
    : question?.type === 'checkbox'
      ? 'multiple_choice'
      : question?.type === 'short_text'
        ? 'text'
        : question?.type || 'single_choice';
  const blank = createBlankQuestion(type, index);
  const options = Array.isArray(question?.options) && question.options.length
    ? question.options.map(optionFrom)
    : blank.options;
  const resolveOptionId = (answer) => {
    const match = options.find((option) => [option.id, option.value, option.label].some((value) => String(value) === String(answer)));
    return match?.id || answer;
  };
  const correctAnswers = Array.isArray(question?.correctAnswers)
    ? question.correctAnswers.map(resolveOptionId)
    : Array.isArray(question?.correctAnswer)
      ? question.correctAnswer.map(resolveOptionId)
      : question?.correctAnswer !== undefined && type === 'multiple_choice'
        ? [resolveOptionId(question.correctAnswer)]
        : blank.correctAnswers;

  return {
    ...blank,
    id: question?.id || `q${index + 1}`,
    prompt: question?.prompt || question?.question || '',
    inputType: question?.inputType || blank.inputType,
    options,
    correctAnswer: question?.correctAnswer !== undefined ? resolveOptionId(question.correctAnswer) : blank.correctAnswer,
    correctAnswers,
    acceptableAnswersText: Array.isArray(question?.acceptableAnswers)
      ? question.acceptableAnswers.join('\n')
      : Array.isArray(question?.correctAnswers) && ['text', 'textarea', 'code'].includes(type)
        ? question.correctAnswers.join('\n')
        : question?.correctAnswer && ['text', 'textarea', 'code'].includes(type)
          ? String(question.correctAnswer)
          : blank.acceptableAnswersText,
    caseSensitive: question?.caseSensitive === true,
    tolerance: question?.tolerance ?? blank.tolerance,
    points: question?.points ?? blank.points,
    required: question?.required !== false,
    displayOrder: question?.displayOrder || index + 1,
    skillTagsText: Array.isArray(question?.skillTags) ? question.skillTags.join(', ') : '',
    explanation: question?.explanation || '',
    gradingMode: question?.gradingMode || blank.gradingMode
  };
};

const questionToPayload = (question, index) => {
  const typeMeta = questionTypeOptions.find((item) => item.value === question.type) || questionTypeOptions[0];
  const payload = {
    id: compactId(question.id, `q${index + 1}`),
    prompt: question.prompt.trim(),
    type: question.type,
    inputType: typeMeta.inputType,
    points: Number(question.points || 1),
    required: question.required !== false,
    displayOrder: index + 1,
    skillTags: splitTags(question.skillTagsText),
    explanation: question.explanation.trim() || null,
    gradingMode: question.gradingMode || typeMeta.defaultMode
  };

  if (choiceTypes.includes(question.type)) {
    const options = question.options.map((option, optionIndex) => {
      const id = compactId(option.id || option.label, `o${optionIndex + 1}`);
      return { id, label: option.label.trim(), value: id };
    });
    payload.options = options;
    if (payload.gradingMode === 'auto') {
      if (question.type === 'single_choice') {
        payload.correctAnswer = question.correctAnswer;
      } else {
        payload.correctAnswers = question.correctAnswers;
      }
    }
  }

  if (question.type === 'numeric' && payload.gradingMode === 'auto') {
    payload.correctAnswer = Number(question.correctAnswer);
    payload.tolerance = Number(question.tolerance || 0);
  }

  if (question.type === 'boolean' && payload.gradingMode === 'auto') {
    payload.correctAnswer = question.correctAnswer === true || String(question.correctAnswer) === 'true';
  }

  if (['text', 'textarea', 'code'].includes(question.type) && payload.gradingMode === 'auto') {
    payload.acceptableAnswers = splitLines(question.acceptableAnswersText);
    payload.caseSensitive = question.caseSensitive === true;
  }

  return payload;
};

const validateQuestions = (questions) => {
  const nextErrors = {};
  const ids = new Set();

  questions.forEach((question, index) => {
    const key = `question-${index}`;
    const id = compactId(question.id, `q${index + 1}`);
    if (ids.has(id)) nextErrors[key] = 'Question IDs must be unique.';
    ids.add(id);

    if (!question.prompt.trim()) nextErrors[key] = 'Question text is required.';
    if (Number(question.points) < 0 || Number(question.points) > 100) nextErrors[key] = 'Points must be between 0 and 100.';

    if (choiceTypes.includes(question.type)) {
      const labels = question.options.map((option) => option.label.trim()).filter(Boolean);
      if (labels.length < 2) nextErrors[key] = 'Add at least two options.';
      if (question.gradingMode === 'auto' && question.type === 'single_choice' && !question.correctAnswer) {
        nextErrors[key] = 'Choose the correct option.';
      }
      if (question.gradingMode === 'auto' && question.type === 'multiple_choice' && !question.correctAnswers.length) {
        nextErrors[key] = 'Choose at least one correct option.';
      }
    }

    if (question.gradingMode === 'auto' && question.type === 'numeric' && question.correctAnswer === '') {
      nextErrors[key] = 'Enter the correct numeric answer.';
    }
    if (question.gradingMode === 'auto' && ['text', 'textarea', 'code'].includes(question.type) && splitLines(question.acceptableAnswersText).length === 0) {
      nextErrors[key] = 'Enter at least one acceptable answer or switch to manual review.';
    }
  });

  return nextErrors;
};

const QuestionEditor = ({ question, index, total, error, onChange, onMove, onDuplicate, onRemove }) => {
  const meta = questionTypeOptions.find((item) => item.value === question.type) || questionTypeOptions[0];
  const TypeIcon = meta.icon;
  const update = (patch) => onChange(index, { ...question, ...patch });
  const isAuto = question.gradingMode !== 'manual';

  const changeType = (nextType) => {
    const next = createBlankQuestion(nextType, index);
    onChange(index, {
      ...next,
      id: question.id,
      prompt: question.prompt,
      points: question.points,
      required: question.required,
      skillTagsText: question.skillTagsText,
      explanation: question.explanation
    });
  };

  const updateOption = (optionIndex, label) => {
    const options = question.options.map((option, currentIndex) => (
      currentIndex === optionIndex ? { ...option, label } : option
    ));
    update({ options });
  };

  const addOption = () => {
    const nextIndex = question.options.length;
    update({
      options: [
        ...question.options,
        { id: `o${nextIndex + 1}`, label: `Option ${nextIndex + 1}`, value: `o${nextIndex + 1}` }
      ]
    });
  };

  const removeOption = (optionId) => {
    const options = question.options.filter((option) => option.id !== optionId);
    const patch = { options };
    if (question.correctAnswer === optionId) patch.correctAnswer = options[0]?.id || '';
    if (question.correctAnswers.includes(optionId)) patch.correctAnswers = question.correctAnswers.filter((id) => id !== optionId);
    update(patch);
  };

  const toggleCorrectAnswer = (optionId) => {
    if (question.type === 'single_choice') {
      update({ correctAnswer: optionId });
      return;
    }
    const current = new Set(question.correctAnswers);
    if (current.has(optionId)) {
      current.delete(optionId);
    } else {
      current.add(optionId);
    }
    update({ correctAnswers: Array.from(current) });
  };

  return (
    <div className={`rounded-lg border bg-white shadow-[0_10px_24px_rgba(15,23,42,0.035)] ${error ? 'border-rose-200' : 'border-slate-200'}`}>
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex items-center justify-center text-sky-600">
            <TypeIcon size={24} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#0b1f3a]">Question {index + 1}</div>
            <div className="text-xs text-slate-500">{meta.label} · {question.gradingMode === 'manual' ? 'Manual review' : 'Auto graded'}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:bg-slate-50 disabled:opacity-40" title="Move up">
            <PiArrowUpLight size={12} />
          </button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:bg-slate-50 disabled:opacity-40" title="Move down">
            <PiArrowDownLight size={12} />
          </button>
          <button type="button" onClick={() => onDuplicate(index)} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:bg-slate-50" title="Duplicate">
            <PiCopyLight size={12} />
          </button>
          <button type="button" onClick={() => onRemove(index)} disabled={total <= 1} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40" title="Delete">
            <PiTrashLight size={12} />
          </button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
            <PiWarningCircleLight size={12} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <Field label="Question">
            <textarea
              value={question.prompt}
              onChange={(event) => update({ prompt: event.target.value })}
              className="min-h-[86px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              placeholder="Write the question candidates will answer"
            />
          </Field>
          <div className="grid gap-3">
            <Field label="Type">
              <select value={question.type} onChange={(event) => changeType(event.target.value)} className={`${inputClass} w-full`}>
                {questionTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
            <Field label="Grading">
              <select value={question.gradingMode} onChange={(event) => update({ gradingMode: event.target.value })} className={`${inputClass} w-full`}>
                <option value="auto">Auto graded</option>
                <option value="manual">Manual review</option>
              </select>
            </Field>
          </div>
        </div>

        {choiceTypes.includes(question.type) && (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Options</div>
            {question.options.map((option, optionIndex) => {
              const checked = question.type === 'single_choice'
                ? question.correctAnswer === option.id
                : question.correctAnswers.includes(option.id);
              return (
                <div key={option.id} className="flex items-center gap-2">
                  <label className="flex h-10 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                    <input
                      type={question.type === 'single_choice' ? 'radio' : 'checkbox'}
                      checked={checked}
                      disabled={!isAuto}
                      onChange={() => toggleCorrectAnswer(option.id)}
                      className="h-4 w-4 border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                  </label>
                  <input
                    value={option.label}
                    onChange={(event) => updateOption(optionIndex, event.target.value)}
                    className={`${inputClass} w-full min-w-0`}
                    placeholder={`Option ${optionIndex + 1}`}
                  />
                  <button type="button" onClick={() => removeOption(option.id)} disabled={question.options.length <= 2} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40">
                    <PiXCircleLight size={13} />
                  </button>
                </div>
              );
            })}
            <button type="button" onClick={addOption} className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50">
              <PiPlusLight size={12} /> Add option
            </button>
          </div>
        )}

        {question.type === 'numeric' && isAuto && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Correct answer">
              <input type="number" value={question.correctAnswer} onChange={(event) => update({ correctAnswer: event.target.value })} className={`${inputClass} w-full`} />
            </Field>
            <Field label="Tolerance">
              <input type="number" min="0" step="0.01" value={question.tolerance} onChange={(event) => update({ tolerance: event.target.value })} className={`${inputClass} w-full`} />
            </Field>
          </div>
        )}

        {question.type === 'boolean' && isAuto && (
          <Field label="Correct answer">
            <select value={String(question.correctAnswer)} onChange={(event) => update({ correctAnswer: event.target.value === 'true' })} className={`${inputClass} w-full max-w-xs`}>
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          </Field>
        )}

        {['text', 'textarea', 'code'].includes(question.type) && isAuto && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
            <Field label="Acceptable answers">
              <textarea
                value={question.acceptableAnswersText}
                onChange={(event) => update({ acceptableAnswersText: event.target.value })}
                className={textareaClass}
                placeholder="One accepted answer per line"
              />
            </Field>
            <label className="flex h-10 items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-[#0b1f3a] lg:mt-6">
              Case sensitive
              <input type="checkbox" checked={question.caseSensitive} onChange={(event) => update({ caseSensitive: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
            </label>
          </div>
        )}

        {question.gradingMode === 'manual' && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
            This question will be scored in the employer review queue after the candidate submits.
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[120px_140px_minmax(0,1fr)]">
          <Field label="Points">
            <input type="number" min="0" max="100" step="0.25" value={question.points} onChange={(event) => update({ points: event.target.value })} className={`${inputClass} w-full`} />
          </Field>
          <label className="flex h-10 items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-[#0b1f3a] lg:mt-6">
            Required
            <input type="checkbox" checked={question.required} onChange={(event) => update({ required: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
          </label>
          <Field label="Skill tags">
            <input value={question.skillTagsText} onChange={(event) => update({ skillTagsText: event.target.value })} className={`${inputClass} w-full`} placeholder="Excel, stakeholder management, Python" />
          </Field>
        </div>

        <Field label="Explanation for reviewers">
          <input value={question.explanation} onChange={(event) => update({ explanation: event.target.value })} className={`${inputClass} w-full`} placeholder="Why this answer is correct or what to look for during review" />
        </Field>
      </div>
    </div>
  );
};

const AssessmentModal = ({ assessment, jobs, types, onClose, onSaved }) => {
  const initialQuestions = assessment?.questions?.length
    ? assessment.questions.map(normalizeQuestionForForm)
    : [createBlankQuestion('single_choice', 0)];
  const [form, setForm] = useState(() => ({
    ...defaultForm,
    jobPostingId: assessment?.jobPostingId || assessment?.job?.id || jobs[0]?.id || '',
    assessmentTitle: assessment?.title || assessment?.assessmentTitle || defaultForm.assessmentTitle,
    assessmentType: assessment?.type || defaultForm.assessmentType,
    description: assessment?.description || '',
    instructions: assessment?.instructions || '',
    timeLimitMinutes: assessment?.timeLimitMinutes || defaultForm.timeLimitMinutes,
    questionCount: assessment?.questionCount || defaultForm.questionCount,
    passingScore: assessment?.passingScore ?? defaultForm.passingScore,
    maxAttempts: assessment?.maxAttempts || defaultForm.maxAttempts,
    isTimed: assessment?.isTimed !== false,
    preventCopyPaste: assessment?.preventCopyPaste !== false,
    randomizeQuestions: assessment?.randomizeQuestions === true,
    showResultsImmediately: assessment?.showResultsImmediately !== false,
    resultVisibility: assessment?.resultVisibility || defaultForm.resultVisibility,
    questionMode: assessment?.questions?.length ? 'custom' : defaultForm.questionMode,
    questions: initialQuestions
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState('single_choice');

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const updateQuestion = (index, nextQuestion) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, currentIndex) => (
        currentIndex === index ? { ...nextQuestion, displayOrder: currentIndex + 1 } : question
      ))
    }));
    setErrors((current) => ({ ...current, [`question-${index}`]: '' }));
  };

  const addQuestion = (type = addType) => {
    setForm((current) => ({
      ...current,
      questionMode: 'custom',
      questions: [...current.questions, createBlankQuestion(type, current.questions.length)]
    }));
  };

  const duplicateQuestion = (index) => {
    setForm((current) => {
      const source = current.questions[index];
      const copy = {
        ...source,
        id: `${compactId(source.id, `q${index + 1}`)}_copy`,
        prompt: `${source.prompt} copy`,
        options: source.options.map((option) => ({ ...option }))
      };
      const questions = [...current.questions];
      questions.splice(index + 1, 0, copy);
      return { ...current, questions: questions.map((question, questionIndex) => ({ ...question, displayOrder: questionIndex + 1 })) };
    });
  };

  const moveQuestion = (index, direction) => {
    setForm((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.questions.length) return current;
      const questions = [...current.questions];
      [questions[index], questions[target]] = [questions[target], questions[index]];
      return { ...current, questions: questions.map((question, questionIndex) => ({ ...question, displayOrder: questionIndex + 1 })) };
    });
  };

  const removeQuestion = (index) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.filter((_, currentIndex) => currentIndex !== index).map((question, questionIndex) => ({
        ...question,
        displayOrder: questionIndex + 1
      }))
    }));
  };

  const buildPayload = () => {
    const customQuestions = form.questionMode === 'custom';
    const payload = {
      assessmentTitle: form.assessmentTitle.trim(),
      assessmentType: form.assessmentType,
      description: form.description.trim() || null,
      instructions: form.instructions.trim() || null,
      timeLimitMinutes: Number(form.timeLimitMinutes),
      questionCount: customQuestions ? form.questions.length : Number(form.questionCount),
      passingScore: Number(form.passingScore),
      maxAttempts: Number(form.maxAttempts),
      isTimed: Boolean(form.isTimed),
      preventCopyPaste: Boolean(form.preventCopyPaste),
      randomizeQuestions: Boolean(form.randomizeQuestions),
      showResultsImmediately: Boolean(form.showResultsImmediately),
      resultVisibility: form.resultVisibility
    };

    if (customQuestions) {
      payload.questions = form.questions.map(questionToPayload);
    }

    return payload;
  };

  const validate = () => {
    const next = {};
    if (!form.jobPostingId) next.jobPostingId = 'Select a job.';
    if (!form.assessmentTitle.trim()) next.assessmentTitle = 'Title is required.';
    if (Number(form.timeLimitMinutes) < 1) next.timeLimitMinutes = 'Time limit must be at least 1 minute.';
    if (Number(form.questionCount) < 1) next.questionCount = 'Question count must be at least 1.';
    if (Number(form.passingScore) < 0 || Number(form.passingScore) > 100) next.passingScore = 'Passing score must be 0-100.';
    if (Number(form.maxAttempts) < 1 || Number(form.maxAttempts) > 5) next.maxAttempts = 'Max attempts must be 1-5.';
    if (form.questionMode === 'custom') {
      if (!form.questions.length) next.questionMode = 'Add at least one question or use backend defaults.';
      Object.assign(next, validateQuestions(form.questions));
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = buildPayload();
      if (assessment?.id) {
        await assessmentAPI.updateForJob(form.jobPostingId, assessment.id, payload);
        toast.success('Assessment updated.');
      } else {
        await assessmentAPI.createForJob(form.jobPostingId, payload);
        toast.success('Assessment created.');
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Failed to save assessment'));
    } finally {
      setSaving(false);
    }
  };

  const visibleTypes = types.length ? types : fallbackAssessmentTypes;
  const totalPoints = form.questionMode === 'custom'
    ? form.questions.reduce((sum, question) => sum + Number(question.points || 0), 0)
    : Number(form.questionCount || 0);
  const autoCount = form.questions.filter((question) => question.gradingMode !== 'manual').length;
  const manualCount = form.questions.length - autoCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <form onSubmit={handleSubmit} className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{assessment?.id ? 'Edit Assessment' : 'New Assessment'}</h2>
            <p className="mt-1 text-sm text-slate-500">Create candidate-ready questions with answer keys, manual review, scoring, and attempt controls.</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-2">
          <Field label="Job" error={errors.jobPostingId}>
            <select value={form.jobPostingId} onChange={(event) => update('jobPostingId', event.target.value)} disabled={Boolean(assessment?.id)} className={`${inputClass} w-full disabled:bg-slate-50`}>
              <option value="">Select job</option>
              {jobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select value={form.assessmentType} onChange={(event) => update('assessmentType', event.target.value)} className={`${inputClass} w-full`}>
              {visibleTypes.map((type) => <option key={type.type || type.value} value={type.type || type.value}>{type.label || labelize(type.type || type.value)}</option>)}
            </select>
          </Field>
          <Field label="Title" error={errors.assessmentTitle}>
            <input value={form.assessmentTitle} onChange={(event) => update('assessmentTitle', event.target.value)} className={`${inputClass} w-full`} />
          </Field>
          <Field label="Result visibility">
            <select value={form.resultVisibility} onChange={(event) => update('resultVisibility', event.target.value)} className={`${inputClass} w-full`}>
              <option value="score_only">Score only</option>
              <option value="score_and_breakdown">Score and breakdown</option>
              <option value="full_review">Full review</option>
            </select>
          </Field>
          <Field label="Time limit" error={errors.timeLimitMinutes}>
            <input type="number" min="1" max="300" value={form.timeLimitMinutes} onChange={(event) => update('timeLimitMinutes', event.target.value)} className={`${inputClass} w-full`} />
          </Field>
          <Field label="Question count" error={errors.questionCount}>
            <input type="number" min="1" max="100" value={form.questionCount} onChange={(event) => update('questionCount', event.target.value)} className={`${inputClass} w-full`} />
          </Field>
          <Field label="Passing score" error={errors.passingScore}>
            <input type="number" min="0" max="100" value={form.passingScore} onChange={(event) => update('passingScore', event.target.value)} className={`${inputClass} w-full`} />
          </Field>
          <Field label="Max attempts">
            <input type="number" min="1" max="5" value={form.maxAttempts} onChange={(event) => update('maxAttempts', event.target.value)} className={`${inputClass} w-full`} />
          </Field>

          <div className="grid gap-3 md:col-span-2 md:grid-cols-4">
            {[
              ['isTimed', 'Timed'],
              ['preventCopyPaste', 'Prevent copy/paste'],
              ['randomizeQuestions', 'Randomize'],
              ['showResultsImmediately', 'Show results']
            ].map(([field, label]) => (
              <label key={field} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-[#0b1f3a]">
                {label}
                <input type="checkbox" checked={Boolean(form[field])} onChange={(event) => update(field, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
              </label>
            ))}
          </div>

          <Field label="Description">
            <textarea value={form.description} onChange={(event) => update('description', event.target.value)} className={textareaClass} />
          </Field>
          <Field label="Instructions">
            <textarea value={form.instructions} onChange={(event) => update('instructions', event.target.value)} className={textareaClass} />
          </Field>
          <div className="md:col-span-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-sm font-medium text-[#0b1f3a]">Question set</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {form.questionMode === 'custom'
                      ? `${form.questions.length} questions · ${totalPoints} total points · ${manualCount} manual review`
                      : `${form.questionCount} backend-generated questions`}
                  </div>
                </div>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => update('questionMode', 'custom')}
                    className={`h-8 rounded-md px-3 text-xs font-medium ${form.questionMode === 'custom' ? 'bg-[#0b1f3a] text-white' : 'text-slate-500'}`}
                  >
                    Build questions
                  </button>
                  <button
                    type="button"
                    onClick={() => update('questionMode', 'default')}
                    className={`h-8 rounded-md px-3 text-xs font-medium ${form.questionMode === 'default' ? 'bg-[#0b1f3a] text-white' : 'text-slate-500'}`}
                  >
                    Use defaults
                  </button>
                </div>
              </div>
              {errors.questionMode && <div className="mt-3 text-xs font-medium text-rose-600">{errors.questionMode}</div>}
            </div>
          </div>

          {form.questionMode === 'custom' ? (
            <div className="md:col-span-2">
              <div className="mb-3 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="grid grid-cols-3 gap-2 text-center sm:w-[360px]">
                  <div className="rounded-md bg-slate-50 px-2 py-2">
                    <div className="text-sm font-medium text-[#0b1f3a]">{form.questions.length}</div>
                    <div className="text-[11px] text-slate-400">Questions</div>
                  </div>
                  <div className="rounded-md bg-slate-50 px-2 py-2">
                    <div className="text-sm font-medium text-[#0b1f3a]">{autoCount}</div>
                    <div className="text-[11px] text-slate-400">Auto</div>
                  </div>
                  <div className="rounded-md bg-slate-50 px-2 py-2">
                    <div className="text-sm font-medium text-[#0b1f3a]">{manualCount}</div>
                    <div className="text-[11px] text-slate-400">Manual</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <select value={addType} onChange={(event) => setAddType(event.target.value)} className={inputClass}>
                    {questionTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button type="button" onClick={() => addQuestion()} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c]">
                    <PiPlusLight size={13} /> Add question
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {form.questions.map((question, index) => (
                  <QuestionEditor
                    key={`${question.id}-${index}`}
                    question={question}
                    index={index}
                    total={form.questions.length}
                    error={errors[`question-${index}`]}
                    onChange={updateQuestion}
                    onMove={moveQuestion}
                    onDuplicate={duplicateQuestion}
                    onRemove={removeQuestion}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="md:col-span-2">
              <div className="rounded-lg border border-sky-100 bg-[#eef9ff] p-4 text-sm leading-6 text-sky-900">
                The backend will generate and snapshot the configured default question set for this assessment type and question count. Switch to Build questions when you need role-specific manual questions or custom answer keys.
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-4 sm:px-5">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
            {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiCheckCircleLight size={13} />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

const InviteModal = ({ assessment, onClose, onInvited }) => {
  const [matches, setMatches] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [message, setMessage] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxAttemptsOverride, setMaxAttemptsOverride] = useState('');
  const [dueAtError, setDueAtError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadMatches = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const response = await candidateAPI.getEmployerCandidates({
        jobId: assessment.jobPostingId || assessment.job?.id,
        matchStatus: 'mutual',
        minScore: 0,
        limit: pagination.limit,
        offset,
        search: candidateSearch.trim() || undefined,
        sortBy: 'match_score',
        sortOrder: 'DESC'
      });
      setMatches(Array.isArray(response.candidates) ? response.candidates : []);
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (error) {
      toast.error(extractError(error, 'Failed to load mutual matches'));
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [assessment, candidateSearch, pagination.limit]);

  useEffect(() => {
    loadMatches(0);
  }, [loadMatches]);

  const selectedSet = useMemo(() => new Set(selectedMatchIds.map(String)), [selectedMatchIds]);
  const visibleSelectedCount = matches.filter((match) => selectedSet.has(String(match.matchId))).length;
  const allVisibleSelected = matches.length > 0 && visibleSelectedCount === matches.length;

  const toggleSelected = (matchId) => {
    setSelectedMatchIds((current) => (
      current.map(String).includes(String(matchId))
        ? current.filter((id) => String(id) !== String(matchId))
        : [...current, matchId]
    ));
  };

  const toggleVisible = () => {
    setSelectedMatchIds((current) => {
      const currentSet = new Set(current.map(String));
      if (allVisibleSelected) {
        return current.filter((id) => !matches.some((match) => String(match.matchId) === String(id)));
      }
      matches.forEach((match) => currentSet.add(String(match.matchId)));
      return Array.from(currentSet);
    });
  };

  const handleInvite = async () => {
    if (!selectedMatchIds.length) return;
    if (dueAt && new Date(dueAt) <= new Date()) {
      setDueAtError('Due date must be in the future.');
      return;
    }
    if (expiresAt && new Date(expiresAt) <= new Date()) {
      setDueAtError('Expiry must be in the future.');
      return;
    }
    setSending(true);
    try {
      const jobId = assessment.jobPostingId || assessment.job?.id;
      const batches = [];
      for (let index = 0; index < selectedMatchIds.length; index += 500) {
        batches.push(selectedMatchIds.slice(index, index + 500));
      }

      let successful = 0;
      let failed = 0;
      for (const batch of batches) {
        const response = await assessmentAPI.inviteCandidates(jobId, assessment.id, {
          matchIds: batch.map(Number),
          message: message || null,
          dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          maxAttemptsOverride: maxAttemptsOverride ? Number(maxAttemptsOverride) : undefined
        });
        successful += Number(response.summary?.successful || 0);
        failed += Number(response.summary?.failed || 0);
      }

      if (successful > 0) {
        toast.success(`${formatNumber(successful)} invitation${successful === 1 ? '' : 's'} queued.`);
      }
      if (failed > 0) {
        toast.error(`${formatNumber(failed)} invitation${failed === 1 ? '' : 's'} could not be queued.`);
      }
      onInvited();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Failed to invite candidate'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Invite Candidates</h2>
            <p className="mt-1 text-sm text-slate-500">{assessment.title} · {assessment.job?.title} · {formatNumber(pagination.total)} mutual matches</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 rounded-lg border border-slate-200">
              <div className="flex flex-col gap-3 border-b border-slate-100 p-3 md:flex-row md:items-center md:justify-between">
                <div className="relative min-w-0 flex-1">
                  <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                  <input
                    value={candidateSearch}
                    onChange={(event) => {
                      setCandidateSearch(event.target.value);
                      setPagination((current) => ({ ...current, offset: 0 }));
                    }}
                    className={`${inputClass} w-full pl-9`}
                    placeholder="Search by candidate, email, location, skill, or role"
                  />
                </div>
                <button type="button" onClick={toggleVisible} disabled={!matches.length} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                  <PiCheckSquareLight size={12} /> {allVisibleSelected ? 'Clear page' : 'Select page'}
                </button>
              </div>

              {loading ? (
                <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-500">
                  <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading candidates
                </div>
              ) : matches.length === 0 ? (
                <EmptyState title="No mutual matches" copy="Assessment invitations are available after both employer and candidate have expressed interest." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left">
                    <thead>
                      <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                        <th className="w-12 px-3 py-3"></th>
                        <th className="px-3 py-3">Candidate</th>
                        <th className="px-3 py-3">Match</th>
                        <th className="px-3 py-3">Evidence</th>
                        <th className="px-3 py-3">Workflow</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((match) => {
                        const candidateName = match.candidate?.fullName || 'Candidate';
                        const selected = selectedSet.has(String(match.matchId));
                        const skills = match.candidate?.skills || [];
                        return (
                          <tr key={match.matchId} className={`border-b border-slate-100 last:border-0 ${selected ? 'bg-sky-50/50' : ''}`}>
                            <td className="px-3 py-3 align-top">
                              <input type="checkbox" checked={selected} onChange={() => toggleSelected(match.matchId)} className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-xs font-medium text-sky-700">
                                  {initialsFor(candidateName)}
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium text-[#0b1f3a]">{candidateName}</div>
                                  <div className="mt-1 text-xs text-slate-500">{match.candidate?.location || 'No location'} · {match.candidate?.experienceYears ?? 0} yrs</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top text-sm text-slate-600">
                              <div className="font-medium text-[#0b1f3a]">{Math.round(Number(match.overallMatchScore || 0))}%</div>
                              <div className="text-xs text-slate-500">Band {match.matchBand || '-'} · {labelize(match.matchStatus)}</div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="max-w-[280px] truncate text-xs text-slate-500">
                                {skills.slice(0, 5).map((skill) => skill.name || skill.skill_name || skill).join(', ') || 'No skills surfaced'}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top text-xs text-slate-500">
                              {formatNumber(match.workflow?.assessments?.total || 0)} assessments · {formatNumber(match.workflow?.interviews?.total || 0)} interviews
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  Showing {formatNumber(pagination.offset + (matches.length ? 1 : 0))}-{formatNumber(pagination.offset + matches.length)} of {formatNumber(pagination.total)}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => loadMatches(Math.max(0, pagination.offset - pagination.limit))} disabled={loading || pagination.offset <= 0} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                    Previous
                  </button>
                  <button type="button" onClick={() => loadMatches(pagination.offset + pagination.limit)} disabled={loading || !pagination.hasMore} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Selection</div>
                <div className="mt-2 text-2xl font-medium text-[#0b1f3a]">{formatNumber(selectedMatchIds.length)}</div>
                <div className="text-xs text-slate-500">Candidates selected across pages</div>
                {selectedMatchIds.length > 0 && (
                  <button type="button" onClick={() => setSelectedMatchIds([])} className="mt-3 h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50">
                    Clear selection
                  </button>
                )}
              </div>

              <div className="grid gap-4">
                <Field label="Due date" error={dueAtError}>
                  <input type="datetime-local" value={dueAt} onChange={(event) => {
                    setDueAt(event.target.value);
                    setDueAtError('');
                  }} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Expires at">
                  <input type="datetime-local" value={expiresAt} onChange={(event) => {
                    setExpiresAt(event.target.value);
                    setDueAtError('');
                  }} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Max attempts">
                  <select value={maxAttemptsOverride} onChange={(event) => setMaxAttemptsOverride(event.target.value)} className={`${inputClass} w-full`}>
                    <option value="">Assessment default</option>
                    {[1, 2, 3, 4, 5].map((attempts) => <option key={attempts} value={attempts}>{attempts}</option>)}
                  </select>
                </Field>
                <Field label="Message">
                  <textarea value={message} onChange={(event) => setMessage(event.target.value)} className={textareaClass} />
                </Field>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-4 sm:px-5">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={handleInvite} disabled={!selectedMatchIds.length || sending} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
            {sending ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiPaperPlaneRightLight size={13} />}
            Invite {selectedMatchIds.length ? formatNumber(selectedMatchIds.length) : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewAttemptModal = ({ assessment, result, onClose, onSaved }) => {
  const details = Array.isArray(result?.detailedResults?.details) ? result.detailedResults.details : [];
  const reviewableDetails = details.filter((detail) => detail.requiresManualReview || result?.status === 'reviewed');
  const [questionReviews, setQuestionReviews] = useState(() => (
    reviewableDetails.reduce((acc, detail) => ({
      ...acc,
      [detail.questionId]: {
        awardedPoints: detail.awardedPoints ?? 0,
        isCorrect: detail.isCorrect === true,
        notes: detail.manualReview?.notes || ''
      }
    }), {})
  ));
  const [reviewerNotes, setReviewerNotes] = useState(result?.reviewerNotes || '');
  const [notifyCandidate, setNotifyCandidate] = useState(true);
  const [saving, setSaving] = useState(false);

  const updateReview = (questionId, field, value) => {
    setQuestionReviews((current) => ({
      ...current,
      [questionId]: {
        ...(current[questionId] || {}),
        [field]: value
      }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!reviewableDetails.length) return;

    setSaving(true);
    try {
      await assessmentAPI.reviewAttempt(
        assessment.jobPostingId || assessment.job?.id,
        assessment.id,
        result.attemptId,
        {
          questionReviews: reviewableDetails.map((detail) => ({
            questionId: detail.questionId,
            awardedPoints: Number(questionReviews[detail.questionId]?.awardedPoints || 0),
            isCorrect: Boolean(questionReviews[detail.questionId]?.isCorrect),
            notes: questionReviews[detail.questionId]?.notes || null
          })),
          reviewerNotes: reviewerNotes || null,
          notifyCandidate
        }
      );
      toast.success('Assessment review saved.');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Failed to save assessment review'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <form onSubmit={handleSubmit} className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Review Attempt</h2>
            <p className="mt-1 text-sm text-slate-500">{assessment.title} · Attempt {result.attemptNumber}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          {!reviewableDetails.length ? (
            <EmptyState title="No reviewable questions" copy="This attempt does not include manual-review question details from the scoring engine." />
          ) : (
            reviewableDetails.map((detail) => {
              const questionId = detail.questionId;
              const maxPoints = Number(detail.points || 0);
              const review = questionReviews[questionId] || {};
              const prompt = detail.prompt || detail.question || detail.label || questionId;

              return (
                <div key={questionId} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0b1f3a] break-words">{prompt}</div>
                      <div className="mt-1 text-xs text-slate-500">Question {questionId} · {maxPoints} pts</div>
                      {detail.answer !== undefined && (
                        <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600 break-words">
                          Answer: {Array.isArray(detail.answer) ? detail.answer.join(', ') : String(detail.answer)}
                        </div>
                      )}
                    </div>
                    <span className={`shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium ${detail.requiresManualReview ? 'border-amber-100 text-amber-600' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                      {detail.requiresManualReview ? 'Manual review' : 'Reviewed'}
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-[160px_160px_minmax(0,1fr)]">
                    <Field label="Awarded points">
                      <input
                        type="number"
                        min="0"
                        max={maxPoints || 100}
                        step="0.25"
                        value={review.awardedPoints}
                        onChange={(event) => updateReview(questionId, 'awardedPoints', event.target.value)}
                        className={`${inputClass} w-full`}
                      />
                    </Field>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-[#0b1f3a]">
                      Correct
                      <input
                        type="checkbox"
                        checked={Boolean(review.isCorrect)}
                        onChange={(event) => updateReview(questionId, 'isCorrect', event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                    </label>
                    <Field label="Question notes">
                      <input
                        value={review.notes || ''}
                        onChange={(event) => updateReview(questionId, 'notes', event.target.value)}
                        className={`${inputClass} w-full`}
                      />
                    </Field>
                  </div>
                </div>
              );
            })
          )}

          <Field label="Reviewer notes">
            <textarea value={reviewerNotes} onChange={(event) => setReviewerNotes(event.target.value)} className={textareaClass} />
          </Field>

          <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-[#0b1f3a]">
            Notify candidate when review is saved
            <input type="checkbox" checked={notifyCandidate} onChange={(event) => setNotifyCandidate(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-4 sm:px-5">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving || !reviewableDetails.length} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
            {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiShieldCheckLight size={13} />}
            Save review
          </button>
        </div>
      </form>
    </div>
  );
};

const AttemptDetailsModal = ({ result, onClose, onReview }) => {
  const details = Array.isArray(result?.detailedResults?.details) ? result.detailedResults.details : [];
  const warnings = Array.isArray(result?.proctoring?.warningEvents) ? result.proctoring.warningEvents : [];
  const pauses = Array.isArray(result?.proctoring?.pauseEvents) ? result.proctoring.pauseEvents : [];
  const candidateName = [result?.candidate?.firstName, result?.candidate?.lastName].filter(Boolean).join(' ') || result?.candidate?.email || 'Candidate';
  const reviewable = details.some((detail) => detail.requiresManualReview) || result?.status === 'reviewed';

  const answerLabel = (value) => {
    if (value === null || value === undefined || value === '') return 'No answer';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Candidate Attempt</h2>
            <p className="mt-1 text-sm text-slate-500">{candidateName} · Attempt {result.attemptNumber}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="space-y-5 p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ['Status', labelize(result.status)],
              ['Score', result.score === null || result.score === undefined ? 'No score' : `${result.score}%`],
              ['Passed', result.passed ? 'Yes' : 'No'],
              ['Warnings', `${result.proctoring?.warningCount ?? 0}/${result.proctoring?.warningLimit ?? 0}`],
              ['Match', `${Math.round(Number(result.matchScore || 0))}%`]
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
                <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{value}</div>
              </div>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-100 bg-white p-3">
              <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Attempt timing</div>
              <div className="mt-3 grid gap-2 text-sm text-slate-600">
                <div>Assigned: <span className="font-medium text-[#0b1f3a]">{formatDateTime(result.assignedAt)}</span></div>
                <div>Started: <span className="font-medium text-[#0b1f3a]">{formatDateTime(result.startedAt)}</span></div>
                <div>Due: <span className="font-medium text-[#0b1f3a]">{formatDateTime(result.dueAt)}</span></div>
                <div>Submitted: <span className="font-medium text-[#0b1f3a]">{formatDateTime(result.submittedAt || result.completedAt)}</span></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-white p-3">
              <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Review state</div>
              <div className="mt-3 grid gap-2 text-sm text-slate-600">
                <div>Manual questions: <span className="font-medium text-[#0b1f3a]">{result.detailedResults?.manualReviewCount ?? details.filter((detail) => detail.requiresManualReview).length}</span></div>
                <div>Reviewed: <span className="font-medium text-[#0b1f3a]">{formatDateTime(result.reviewedAt)}</span></div>
                <div>Reviewer: <span className="font-medium text-[#0b1f3a]">{[result.reviewedBy?.firstName, result.reviewedBy?.lastName].filter(Boolean).join(' ') || 'Not assigned'}</span></div>
              </div>
              {result.reviewerNotes && <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">{result.reviewerNotes}</div>}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-display text-lg font-normal text-[#0b1f3a]">Question Results</h3>
              {reviewable && (
                <button type="button" onClick={onReview} className="inline-flex h-9 items-center gap-2 rounded-lg bg-amber-500 px-3 text-xs font-medium text-white transition hover:bg-amber-600 btn-premium-base btn-premium-highlight-amber">
                  <PiPencilSimpleLight size={12} /> Open review
                </button>
              )}
            </div>
            {details.length ? (
              <div className="space-y-3">
                {details.map((detail, index) => (
                  <div key={detail.questionId || index} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#0b1f3a] break-words">{detail.prompt || detail.questionId}</div>
                        <div className="mt-1 text-xs text-slate-500">{labelize(detail.type)} · {detail.awardedPoints ?? 0}/{detail.points ?? 0} pts</div>
                      </div>
                      <span className={`shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium ${detail.requiresManualReview ? 'border-amber-100 text-amber-600' : detail.isCorrect ? 'border-emerald-100 text-emerald-600' : 'border-rose-100 bg-rose-50 text-rose-700'}`}>
                        {detail.requiresManualReview ? 'Manual review' : detail.isCorrect ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600 break-words">
                      Answer: {answerLabel(detail.userAnswer ?? detail.answer)}
                    </div>
                    {detail.explanation && <div className="mt-2 text-xs leading-5 text-slate-500 break-words">{detail.explanation}</div>}
                    {Array.isArray(detail.skillTags) && detail.skillTags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {detail.skillTags.map((tag) => <span key={tag} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-500">{tag}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No question details" copy="This attempt has not produced a detailed result payload yet." />
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Warning events</div>
              {warnings.length ? warnings.map((event, index) => (
                <div key={`${event.type}-${index}`} className="mb-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                  {labelize(event.type)} · {formatDateTime(event.recordedAt)}{event.message ? ` · ${event.message}` : ''}
                </div>
              )) : <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">No warnings recorded.</div>}
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Pause events</div>
              {pauses.length ? pauses.map((event, index) => (
                <div key={`${event.reason}-${index}`} className="mb-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  {labelize(event.reason)} · {formatDateTime(event.pausedAt || event.resumedAt)}
                </div>
              )) : <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">No pauses recorded.</div>}
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-100 px-4 py-4 sm:px-5">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ResultsModal = ({ assessment, refreshToken = 0, onClose, onDetails, onReview }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  const jobId = assessment?.jobPostingId || assessment?.job?.id;

  const loadResults = useCallback(async (offset = 0) => {
    if (!assessment || !jobId) return;
    setLoading(true);
    try {
      const response = await assessmentAPI.getResults(jobId, assessment.id, {
        limit: pagination.limit,
        offset,
        search: search.trim() || undefined,
        status
      });
      setRows(Array.isArray(response.results) ? response.results : []);
      setSummary(response.summary || null);
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (error) {
      toast.error(extractError(error, 'Failed to load assessment results'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [assessment, jobId, pagination.limit, search, status]);

  useEffect(() => {
    loadResults(0);
  }, [loadResults, refreshToken]);

  const metrics = [
    ['Candidates', summary?.total || pagination.total || 0],
    ['Invited', summary?.invited || 0],
    ['Completed', summary?.completed || 0],
    ['Passed', summary?.passed || 0],
    ['Manual review', summary?.manualReview || 0],
    ['Average', summary?.averageScore === null || summary?.averageScore === undefined ? 'No score' : `${summary.averageScore}%`]
  ];

  const canView = (result) => Boolean(result.attemptId);
  const canReview = (result) => {
    const details = Array.isArray(result?.detailedResults?.details) ? result.detailedResults.details : [];
    return canView(result) && (details.some((detail) => detail.requiresManualReview) || result.status === 'reviewed');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <div className="max-h-[92vh] w-full max-w-7xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Assessment Results</h2>
            <p className="mt-1 text-sm text-slate-500">{assessment.title} · {assessment.job?.title || 'Job'} · {labelize(assessment.type)}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {metrics.map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
                <div className="mt-1 text-lg font-medium text-[#0b1f3a]">{typeof value === 'number' ? formatNumber(value) : value}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPagination((current) => ({ ...current, offset: 0 }));
                }}
                className={`${inputClass} w-full pl-9`}
                placeholder="Search candidate name or email"
              />
            </div>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPagination((current) => ({ ...current, offset: 0 }));
              }}
              className={inputClass}
            >
              <option value="all">All statuses</option>
              <option value="not_invited">Not invited</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="reviewed">Reviewed</option>
              <option value="manual_review">Manual review</option>
              <option value="passed">Passed</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            {loading ? (
              <div className="flex min-h-[360px] items-center justify-center text-sm text-slate-500">
                <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading results
              </div>
            ) : rows.length === 0 ? (
              <EmptyState title="No candidates found" copy="Adjust filters or invite candidates to this assessment." icon={PiUsersLight} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px] text-left">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                      <th className="px-3 py-3">Candidate</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Score</th>
                      <th className="px-3 py-3">Attempt</th>
                      <th className="px-3 py-3">Dates</th>
                      <th className="px-3 py-3">Match</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((result) => {
                      const candidateName = [result.candidate?.firstName, result.candidate?.lastName].filter(Boolean).join(' ') || result.candidate?.email || 'Candidate';
                      const details = Array.isArray(result.detailedResults?.details) ? result.detailedResults.details : [];
                      const manualCount = result.detailedResults?.manualReviewCount ?? details.filter((detail) => detail.requiresManualReview).length;
                      return (
                        <tr key={`${result.matchId}-${result.attemptId || 'none'}`} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-xs font-medium text-sky-700">
                                {initialsFor(candidateName)}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-[#0b1f3a]">{candidateName}</div>
                                <div className="mt-1 truncate text-xs text-slate-500">{result.candidate?.email || 'No email'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${statusTone(result.status)}`}>
                              {labelize(result.status)}
                            </span>
                            {manualCount > 0 && <div className="mt-2 text-xs font-medium text-amber-700">{manualCount} manual</div>}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-600">
                            {result.score === null || result.score === undefined ? 'No score' : `${result.score}%`}
                            {result.passed !== null && result.passed !== undefined && (
                              <div className={result.passed ? 'text-xs text-emerald-700' : 'text-xs text-slate-500'}>{result.passed ? 'Passed' : 'Not passed'}</div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-600">
                            {result.attemptId ? `#${result.attemptNumber || 1}` : 'No attempt'}
                            {result.timeTakenMinutes !== null && result.timeTakenMinutes !== undefined && <div className="text-xs text-slate-500">{result.timeTakenMinutes} min</div>}
                          </td>
                          <td className="px-3 py-3 text-xs leading-5 text-slate-500">
                            <div>Assigned: {formatDate(result.assignedAt)}</div>
                            <div>Submitted: {formatDate(result.submittedAt || result.completedAt)}</div>
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-600">
                            {Math.round(Number(result.matchScore || 0))}%
                            <div className="text-xs text-slate-500">{labelize(result.matchStatus)}</div>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => onDetails(result)}
                                disabled={!canView(result)}
                                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                              >
                                <PiFileTextLight size={12} /> View
                              </button>
                              {canReview(result) && (
                                <button
                                  type="button"
                                  onClick={() => onReview(result)}
                                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-2.5 text-xs font-medium text-white transition hover:bg-amber-600 btn-premium-base btn-premium-highlight-amber"
                                >
                                  <PiPencilSimpleLight size={12} /> Review
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-col gap-3 border-t border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500">
                Showing {formatNumber(pagination.offset + (rows.length ? 1 : 0))}-{formatNumber(pagination.offset + rows.length)} of {formatNumber(pagination.total)}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => loadResults(Math.max(0, pagination.offset - pagination.limit))} disabled={loading || pagination.offset <= 0} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                  Previous
                </button>
                <button type="button" onClick={() => loadResults(pagination.offset + pagination.limit)} disabled={loading || !pagination.hasMore} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssessmentsPage = () => {
  const [assessments, setAssessments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState('true');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalAssessment, setModalAssessment] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [inviteAssessment, setInviteAssessment] = useState(null);
  const [resultsAssessment, setResultsAssessment] = useState(null);
  const [resultsRefreshToken, setResultsRefreshToken] = useState(0);
  const [reviewResult, setReviewResult] = useState(null);
  const [detailResult, setDetailResult] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [isHighlighting, setIsHighlighting] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighting(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [typesResponse, assessmentsResponse, jobsResponse] = await Promise.all([
        assessmentAPI.getTypes(),
        assessmentAPI.getEmployerAssessments({
          ...(jobFilter ? { jobId: jobFilter } : {}),
          ...(activeOnly !== 'all' ? { active: activeOnly } : {})
        }),
        jobAPI.getEmployerJobs({ limit: 100, offset: 0, sortBy: 'created_at', sortOrder: 'DESC' })
      ]);

      const nextAssessments = Array.isArray(assessmentsResponse.assessments) ? assessmentsResponse.assessments : [];
      setTypes(Array.isArray(typesResponse.assessmentTypes) ? typesResponse.assessmentTypes : []);
      setAssessments(nextAssessments);
      setJobs(Array.isArray(jobsResponse.jobs) ? jobsResponse.jobs : []);
      setSelectedAssessmentId((current) => current || String(nextAssessments[0]?.id || ''));
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load assessments'));
    } finally {
      setLoading(false);
    }
  }, [activeOnly, jobFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedAssessment = useMemo(() => (
    assessments.find((assessment) => String(assessment.id) === String(selectedAssessmentId)) || assessments[0] || null
  ), [assessments, selectedAssessmentId]);
  const reviewAssessment = resultsAssessment || selectedAssessment;

  const filteredAssessments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assessments;
    return assessments.filter((assessment) => [
      assessment.title,
      assessment.type,
      assessment.job?.title,
      assessment.description
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query)));
  }, [assessments, search]);

  const metrics = useMemo(() => {
    const totalAttempts = assessments.reduce((sum, item) => sum + Number(item.attemptSummary?.total ?? item.attemptsCount ?? 0), 0);
    const completed = assessments.reduce((sum, item) => sum + Number(item.attemptSummary?.completed ?? item.completedCount ?? 0), 0);
    const passed = assessments.reduce((sum, item) => sum + Number(item.attemptSummary?.passed ?? 0), 0);
    const average = assessments.length
      ? Math.round(assessments.reduce((sum, item) => sum + Number(item.attemptSummary?.averageScore ?? item.averageScore ?? 0), 0) / assessments.length)
      : 0;

    return {
      active: assessments.filter((item) => item.isActive !== false).length,
      totalAttempts,
      completed,
      passed,
      average
    };
  }, [assessments]);

  const handleDeactivate = async (assessment) => {
    if (!window.confirm(`Deactivate "${assessment.title}"? Existing attempts remain available for audit.`)) return;
    setBusyId(assessment.id);
    try {
      await assessmentAPI.deactivate(assessment.jobPostingId || assessment.job?.id, assessment.id);
      toast.success('Assessment deactivated.');
      loadData();
    } catch (error) {
      toast.error(extractError(error, 'Failed to deactivate assessment'));
    } finally {
      setBusyId('');
    }
  };

  if (loading && assessments.length === 0) {
    return <AssessmentsPageSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
          50% { box-shadow: 0 0 0 3px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        @keyframes subtle-pulse-ring-emerald {
          0% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(5, 150, 105, 0); }
          100% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
        }
        @keyframes subtle-pulse-ring-sky {
          0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(2, 132, 199, 0); }
          100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
        }
        @keyframes subtle-pulse-ring-amber {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 2s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-emerald {
          animation: subtle-pulse-ring-emerald 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-sky {
          animation: subtle-pulse-ring-sky 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-amber {
          animation: subtle-pulse-ring-amber 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Assessments</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Assessment Workflow</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Build role assessments, invite mutual-interest candidates, monitor attempts, proctoring state, scores, and review outcomes.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowCreate(true)}
              className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                isHighlighting
                  ? 'bg-black btn-premium-highlight'
                  : 'bg-[#0b1f3a] hover:bg-[#12345c]'
              }`}
            >
              <PiPlusLight size={13} /> New assessment
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Metric icon={PiClipboardTextLight} label="Active assessments" value={formatNumber(metrics.active)} detail="Available" tone="sky" />
          <Metric icon={PiUsersLight} label="Attempts" value={formatNumber(metrics.totalAttempts)} detail="All states" tone="slate" />
          <Metric icon={PiCheckCircleLight} label="Completed" value={formatNumber(metrics.completed)} detail="Submitted/reviewed" tone="green" />
          <Metric icon={PiMedalLight} label="Passed" value={formatNumber(metrics.passed)} detail="Candidate passed" tone="amber" />
          <Metric icon={PiShieldCheckLight} label="Average score" value={`${metrics.average}%`} detail="Assessment mean" tone="slate" />
        </div>

        <div className="grid gap-6">
          <Panel
            title="Assessment Library"
            icon={PiFileTextLight}
            action={(
              <div className="flex items-center gap-2">
                <PiSlidersHorizontalLight size={12} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-500">{filteredAssessments.length} shown</span>
              </div>
            )}
          >
            <div className="mb-4 flex flex-col gap-3 lg:flex-row">
              <div className="relative min-w-0 flex-1">
                <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input value={search} onChange={(event) => setSearch(event.target.value)} className={`${inputClass} w-full pl-9`} placeholder="Search assessments" />
              </div>
              <select value={jobFilter} onChange={(event) => setJobFilter(event.target.value)} className={inputClass}>
                <option value="">All jobs</option>
                {jobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}
              </select>
              <select value={activeOnly} onChange={(event) => setActiveOnly(event.target.value)} className={inputClass}>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
                <option value="all">All states</option>
              </select>
            </div>

            {filteredAssessments.length === 0 ? (
              <EmptyState title="No assessments found" copy="Create an assessment for an active job or adjust filters to include inactive assessments." />
            ) : (
              <div className="overflow-x-auto -mx-4 px-4 sm:-mx-5 sm:px-5">
                <table className="w-full min-w-[980px] text-left">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                      <th className="py-3 pr-4">Assessment</th>
                      <th className="py-3 pr-4">Job</th>
                      <th className="py-3 pr-4">Config</th>
                      <th className="py-3 pr-4">Attempts</th>
                      <th className="py-3 pr-4">Score</th>
                      <th className="py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssessments.map((assessment) => (
                      <tr key={assessment.id} className={`border-b border-slate-100 last:border-0 ${String(selectedAssessment?.id) === String(assessment.id) ? 'bg-sky-50/40' : ''}`}>
                        <td className="py-4 pr-4">
                          <button onClick={() => setSelectedAssessmentId(String(assessment.id))} className="block max-w-[320px] text-left">
                            <span className="block truncate text-sm font-medium text-[#0b1f3a]">{assessment.title}</span>
                            <span className="mt-1 text-xs text-slate-500">{labelize(assessment.type)} · {assessment.isActive === false ? 'Inactive' : 'Active'}</span>
                          </button>
                        </td>
                        <td className="py-4 pr-4 text-sm text-slate-600">{assessment.job?.title || 'No job'}</td>
                        <td className="py-4 pr-4 text-sm text-slate-600">
                          {assessment.questionCount} q · {assessment.timeLimitMinutes} min · {assessment.passingScore}%
                        </td>
                        <td className="py-4 pr-4 text-sm text-slate-600">
                          {formatNumber(assessment.attemptSummary?.total ?? assessment.attemptsCount)} total · {formatNumber(assessment.attemptSummary?.completed ?? assessment.completedCount)} done
                        </td>
                        <td className="py-4 pr-4">
                          <span className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                            {assessment.attemptSummary?.averageScore ?? assessment.averageScore ?? 'No'} avg
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex justify-end gap-1.5">
                            <button onClick={() => {
                              setSelectedAssessmentId(String(assessment.id));
                              setResultsAssessment(assessment);
                            }} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700" title="Results">
                              <PiClipboardTextLight size={12} />
                            </button>
                            <button onClick={() => setInviteAssessment(assessment)} disabled={assessment.isActive === false} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-40" title="Invite">
                              <PiPaperPlaneRightLight size={12} />
                            </button>
                            <button onClick={() => setModalAssessment(assessment)} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700" title="Edit">
                              <PiPencilSimpleLight size={12} />
                            </button>
                            <button onClick={() => handleDeactivate(assessment)} disabled={busyId === assessment.id || assessment.isActive === false} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40" title="Deactivate">
                              {busyId === assessment.id ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiTrashLight size={12} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

        </div>
      </div>

      {(showCreate || modalAssessment) && (
        <AssessmentModal
          assessment={modalAssessment}
          jobs={jobs}
          types={types}
          onClose={() => {
            setShowCreate(false);
            setModalAssessment(null);
          }}
          onSaved={loadData}
        />
      )}

      {inviteAssessment && (
        <InviteModal
          assessment={inviteAssessment}
          onClose={() => setInviteAssessment(null)}
          onInvited={() => {
            loadData();
            setResultsRefreshToken((current) => current + 1);
          }}
        />
      )}

      {resultsAssessment && (
        <ResultsModal
          assessment={resultsAssessment}
          refreshToken={resultsRefreshToken}
          onClose={() => setResultsAssessment(null)}
          onDetails={(result) => setDetailResult(result)}
          onReview={(result) => {
            setSelectedAssessmentId(String(resultsAssessment.id));
            setReviewResult(result);
          }}
        />
      )}

      {reviewResult && reviewAssessment && (
        <ReviewAttemptModal
          assessment={reviewAssessment}
          result={reviewResult}
          onClose={() => setReviewResult(null)}
          onSaved={() => {
            loadData();
            setResultsRefreshToken((current) => current + 1);
          }}
        />
      )}

      {detailResult && (
        <AttemptDetailsModal
          result={detailResult}
          onClose={() => setDetailResult(null)}
          onReview={() => {
            setReviewResult(detailResult);
            setDetailResult(null);
          }}
        />
      )}
    </div>
  );
};

export default AssessmentsPage;
